<template>
  <v-container class="white-bg">
    <v-layout text-center wrap>
      <v-flex xs12>
        <v-alert v-if="errorMessages" type="error">{{ errorMessages }}</v-alert>
        <v-toolbar-title class="headline text-uppercase text-left">
          <span class="font-weight-bold">{{ $t('Order') }} {{ order.orderName }}</span>
          <h6 class="font-weight-light">
            {{ $t('Submitted by') }} {{ order.fullName }} {{ $t('on') }}
            {{ order.dateCreated | formatDate }}
          </h6>
        </v-toolbar-title>
        <v-app>
          <v-data-table
            :headers="computedHeaders"
            :items="order.products"
            show-select
            item-selected
            click
            v-model="selected"
            item-key="rowKey"
            class="second-tab"
          >
            <!-- <template v-slot:header.data-table-select> </template> -->

            <template v-slot:item.image="{ item }">
              <v-img :src="item.image" max-width="55"></v-img>
            </template>

            <template v-slot:top>
              <v-toolbar flat color="white">
                <v-toolbar-title>{{ $t('Products on this order') }}</v-toolbar-title>
                <div class="flex-grow-1"></div>
                <v-dialog v-model="dialog" max-width="1000px">
                  <v-card class="dialog modal quantity-dialog">
                    <v-card-title>
                      <span class="text-h5">{{ $t('Change return quantity') }}</span>
                      <svg
                        @click="close"
                        width="15"
                        height="15"
                        viewBox="0 0 15 15"
                        fill="none"
                        class="close-icon"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <g clip-path="url(#clip0_792_50)">
                          <path
                            d="M1.07576 13.9242C1.1193 13.9678 1.17099 14.0023 1.2279 14.0259C1.2848 14.0495 1.3458 14.0617 1.4074 14.0617C1.469 14.0617 1.53 14.0495 1.58691 14.0259C1.64381 14.0023 1.69551 13.9678 1.73904 13.9242L7.49998 8.16325L13.2633 13.9242C13.3512 14.0121 13.4705 14.0616 13.5949 14.0616C13.7193 14.0616 13.8386 14.0121 13.9265 13.9242C14.0145 13.8362 14.0639 13.7169 14.0639 13.5925C14.0639 13.4682 14.0145 13.3489 13.9265 13.2609L8.16326 7.49997L13.9242 1.73669C14.0122 1.64873 14.0616 1.52944 14.0616 1.40505C14.0616 1.28066 14.0122 1.16136 13.9242 1.07341C13.8362 0.985449 13.7169 0.936035 13.5926 0.936035C13.4682 0.936035 13.3489 0.985449 13.2609 1.07341L7.49998 6.83669L1.7367 1.07575C1.64703 0.998955 1.53168 0.958828 1.41371 0.963384C1.29573 0.967941 1.18382 1.01685 1.10034 1.10033C1.01686 1.18381 0.967954 1.29572 0.963398 1.41369C0.958841 1.53167 0.998969 1.64701 1.07576 1.73669L6.8367 7.49997L1.07576 13.2632C0.988457 13.3511 0.939453 13.4699 0.939453 13.5937C0.939453 13.7176 0.988457 13.8364 1.07576 13.9242Z"
                            fill="black"
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_792_50">
                            <rect width="15" height="15" fill="white" />
                          </clipPath>
                        </defs>
                      </svg>
                    </v-card-title>
                    <v-card-text>
                      <v-row>
                        <v-col cols="12" sm="8" md="8">
                          <v-text-field
                            color="dark"
                            outlined
                            v-model="editedItem.name"
                            :label="$t('Product name')"
                            readonly
                          ></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="4" md="4">
                          <v-text-field
                            color="dark"
                            outlined
                            v-model="editedItem.quantity"
                            type="number"
                            :label="$t('Quantity')"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-card-text>
                    <v-card-actions>
                      <div class="flex-grow-1"></div>
                      <v-btn color="dark" :dark="true" @click="save">{{ $t('Save') }}</v-btn>
                      <v-btn color="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-anulat" @click="close">
                        {{ $t('Cancel') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>

            <template v-slot:item.name="{ item }">
              <div>
                <span class="product-table-name">{{ item.name }}</span>
                <div class="product-table-description">
                  <div class="product-table-desc">
                    <span class="product-table-desc-title">{{ $t('Variant') }}</span>
                    <v-chip>{{ item.variant }}</v-chip>
                  </div>
                  <div class="product-table-desc">
                    <span class="product-table-desc-title">{{ $t('SKU') }}</span> <v-chip>{{ item.sku }}</v-chip>
                  </div>
                </div>
              </div>
            </template>

            <template v-slot:item.quantity="{ item }">
              <v-chip>{{ item.quantity }}</v-chip>
            </template>

            <template v-slot:item.price="{ item }">
              <v-chip class="v-price">{{ getPrice(item.price, order.currency) }}</v-chip>
            </template>

            <template v-slot:item.action="{ item }">
              <edit-icon class="mr-2" @click="editItem(item)" title="Edit product" />
              <delete-icon class="mr-2" @click="deleteItem(item)" title="Delete product" />
            </template>
            <template v-slot:footer>
              <div class="text-left py-5" xs12>
                <v-btn class="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-anulat" @click="goBack">{{
                  $t('Cancel and go back')
                }}</v-btn>
                <v-btn
                  :disabled="true"
                  v-if="selected.length == 0"
                  @click="proceed"
                  class="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-return"
                  >{{ preferences.replaceProduct ? $t('Return or change selected products') : $t('Return selected products') }}
                </v-btn>
                <v-btn
                  color="darken-1"
                  :dark="true"
                  v-else
                  @click="proceed"
                  class="mb-3 mb-sm-0 mr-0 mr-sm-5 btn-return"
                  >{{ preferences.replaceProduct ? $t('Return or change selected products') : $t('Return selected products') }}</v-btn>
              </div>
            </template>
          </v-data-table>
        </v-app>
      </v-flex>
      <div v-if="themeJson.name === 'custom'">
        <div v-html="styles"></div>
      </div>
    </v-layout>
    <v-overlay :value="overlay" :color="'white'">
      <v-progress-circular indeterminate size="64" :color="'black'"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex';
import Vue from 'vue';
import EditIcon from './icons/edit_icon.vue';
import DeleteIcon from './icons/delete_icon.vue';
import moment from 'moment';

Vue.component('edit-icon', EditIcon);
Vue.component('delete-icon', DeleteIcon);

Vue.filter('formatDate', function (value) {
  if (value) {
    return moment(String(value)).format('DD/MM/YYYY HH:mm');
  }
});

export default {
  data: vm => ({
    errorMessages: '',
    overlay: false,
    selected: [],
    editedIndex: -1,
    editedItem: {
      name: '',
      quantity: 0,
    },
    computedHeaders: [],
    styles: '',
    defaultItem: {
      name: '',
      quantity: 0,
    },
    dialog: false,
    test: vm.test
  }),
  computed: {
    ...mapGetters(['order', 'email_address', 'order_number', 'preferences', 'phone_number']),
    ...mapState(['themeJson']),
  },
  watch: {
    overlay(val) {
      val &&
        setTimeout(() => {
          this.overlay = false;
        }, 1000);
    },
  },
  created() {
    this.styles = this.getStyles();
    this.updateHeaders();
    window.addEventListener('resize', this.updateHeaders);
    this.getOrder(this.$route.params.id);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateHeaders);
  },
  mounted() {
    this.overlay = true;
    this.verifyOrder();
  },
  methods: {
    updateHeaders() {
      if (screen.width <= 600) {
        this.computedHeaders = [
          { text: this.$t('Return product'), value: 'data-table-select' },
          { text: this.$t('Image'), value: 'image' },
          { text: this.$t('Product name'), value: 'name' },
          { text: this.$t('Variant'), value: 'variant' },
          { text: this.$t('SKU'), value: 'sku' },
          { text: this.$t('Quantity'), value: 'quantity' },
        ];
      } else {
        this.computedHeaders = [
          { text: this.$t('Return product'), value: 'data-table-select' },
          { text: this.$t('Image'), value: 'image' },
          { text: this.$t('Product name'), value: 'name' },
          { text: this.$t('Quantity'), value: 'quantity' },
          { text: this.$t('Price'), value: 'price' },
        ];
      }

      if ( this.preferences.editQuantity ) {
        this.computedHeaders.push({
          text: this.$t('Actions'),
          value: 'action',
          sortable: false,
        });
      }
    },
    ...mapActions(['getOrder', 'setSelectedProducts']),
    verifyOrder() {
      if ((this.email_address == null || this.phone_number == null) && this.order_number == null) {
        this.$router.push({
          path: '/',
          query: { shop: this.$store.state.shop, lang: this.$i18n.locale },
        });
      } else {
        return;
      }
    },
    getStyles() {
      const theme = this.themeJson;
      return `
<style>
body.theme-custom .dialog .v-input input {
    color: ${theme.inputTextColor};
}

body.theme-custom .v-data-table .v-data-table-header th.sortable .v-data-table-header__icon {
      color:  ${theme.textColor};
}

body.theme-custom .container .v-data-table {
    background: transparent !important;
}

body.theme-custom .container {
    color: ${theme.textColor};
    background: inherit !important;
}

body.theme-custom .v-icon.mdi-menu-down {
      color: ${theme.textColor};
}

body.theme-custom .container .v-application--is-ltr .v-data-footer__pagination {
      color: ${theme.textColor}
}

body.theme-custom .theme--light.v-btn.v-btn--disabled .v-icon {
      color: ${theme.textColor} !important;
}

body.theme-custom .dialog .v-input__slot {
      border-width: ${theme.inputBackgroundColor === '#ffffff' ? '1px' : '0'}
}

body.theme-custom .v-simple-checkbox .v-icon {
      background-color: ${theme.textColor} !important;
      border-radius: 3px;
}

body.theme-custom .v-label {
      color: ${theme.textColor} !important;
}

body.theme-custom .v-toolbar__title {
    color: ${theme.textColor};
}

body.theme-custom .pencil-outline-icon svg path {
      fill: ${theme.textColor} !important;
}
body.theme-custom .material-design-icon svg path {
      fill: ${theme.textColor} !important;
}

body.theme-custom .container .v-select__selection {
    color: ${theme.textColor} !important;
}

body.theme-custom .v-input {
    color: ${theme.textColor} !important;
}

body.theme-custom .container .v-data-table__wrapper th {
    color: ${theme.textColor} !important;
}

body.theme-custom .container .btn-return {
    background: ${theme.sendBtnColor} !important;
    color: ${theme.btnTextColor} !important;
}

body.theme-custom .v-btn.v-btn--has-bg:not(.btn-anulat):not(.btn-return):not(.v-btn--back):not(.v-btn--disabled):not(.v-btn--return):not(.btn-change) {
         background: ${theme.sendBtnColor} !important;
    color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .btn-anulat {
    background: ${theme.cancelBtnColor} !important;
    color: ${theme.btnTextColor} !important;
}

body.theme-custom .dialog .v-card__title {
    color: ${theme.textColor} !important;
}

body.theme-custom .container .v-data-table__wrapper tbody td {
    color: ${theme.textColor};
}

body.theme-custom .dialog fieldset {
    background: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .close-icon path{
    cursor: pointer;
    fill: ${theme.textColor}
}

body.theme-custom .v-application {
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .v-application .white {
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .v-application .v-input__slot fieldset {
    background-color: ${theme.inputBackgroundColor};
}

body.custom-theme .dialog .v-input__slot {
      color: ${theme.textColor} !important;
}

body.theme-custom .container .v-data-table__wrapper tbody tr:nth-child(odd)::after {
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .container .v-chip {
    background: ${theme.backgroundColor} !important;
    color: ${theme.textColor};
}

body.theme-custom .container .v-data-footer__select {
      color: ${theme.textColor} !important;
}

body.theme-custom .container .v-btn.v-btn--disabled.v-btn--has-bg {
      color: ${theme.btnTextColor} !important;
}

body.theme-custom .container .btn-anulat {
    background: ${theme.cancelBtnColor} !important;
    color: ${theme.btnTextColor} !important;
}

body.theme-custom .container .v-select .v-input__slot:before {
    border-color: ${theme.selectBorderColor};
}

body.theme-custom .container .v-select:hover .v-input__slot:before {
    border-color: ${theme.selectBorderColor} !important;
}

body.theme-custom .container .v-data-table__wrapper tbody tr:nth-child(odd)::after {
    background-color: ${theme.inputBackgroundColor} !important;
}

body.theme-custom .dialog {
    color: white !important;
    background-color: ${theme.backgroundColor} !important;
}

body.theme-custom .v-application--is-ltr .v-text-field.v-input--is-focused .v-input__slot {
      border: 1px solid ${theme.selectBorderColor}
}

}
</style>
    `;
    },
    goBack: function () {
      this.$router.push({
        path: '/',
        query: { shop: this.$store.state.shop, lang: this.$i18n.locale },
      });
    },

    proceed() {
      if (!navigator.onLine) {
        this.errorMessages = this.$t('No network connection');
      } else {
        this.setSelectedProducts(this.selected);
        this.$router.push('/submit-return');
      }
    },
    getPrice(price, currency) {
      return price + ' ' + currency;
    },

    editItem(item) {
      this.editedIndex = this.order.products.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.dialog = true;
    },

    deleteItem(item) {
      const index = this.order.products.indexOf(item);
      const text = this.$t('Are you sure you want to delete this item?');
      confirm(text) && this.order.products.splice(index, 1);
    },

    close() {
      this.dialog = false;
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
      }, 300);
    },

    save() {
      if (this.editedIndex > -1) {
        Object.assign(this.order.products[this.editedIndex], this.editedItem);
      } else {
        this.order.products.push(this.editedItem);
      }
      this.close();
    },
  },
};
</script>

<style lang="scss">
.v-data-table-header {
  tr {
    th {
      white-space: nowrap;
    }
  }
}
.white-bg {
  background-color: #fff !important;
}
.theme-dark,
.theme-blue,
.theme-green,
.theme-light-blue,
.theme-light-green {
  .white-bg {
    background-color: inherit !important;
  }
}
.container {
  padding: 12px;
  & {
    max-width: 1160px;
    padding: 12px;
  }
  .white {
    background-color: #fff !important;
  }
  .text-start {
    position: relative;

    .v-simple-checkbox {
      top: inherit !important;
      bottom: 15px;
    }
  }
  .font-weight-bold {
    font-size: 24px;
    font-weight: 400 !important;
  }
  .font-weight-light {
    font-size: 16px;
    font-weight: 400 !important;
    margin-top: 12px;
  }
  .v-toolbar {
    &__content {
      padding: 0;
    }
    &__title {
      font-size: 16px;
      font-weight: 500;
      padding-top: 28px;
      padding-bottom: 28px;
    }
  }
  .v-data-table {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;
    @media screen and (max-width: 920px) {
      .v-toolbar {
        order: 1;
      }
      .v-data-table__wrapper {
        order: 2;
      }
      .text-left.py-5 {
        order: 4;
        padding-top: 0 !important;
        width: 100%;
      }
      .v-data-footer {
        order: 3;
      }
    }
    .text-left {
      display: flex;
      column-gap: 17px;
      max-width: 100%;
      flex-wrap: wrap;
      align-items: center;
      justify-content: center;
      .btn-anulat {
        margin-right: 0 !important;
      }
    }
    &__wrapper {
      width: 100%;

      & th {
        font-size: 12px !important;
        font-weight: 600;
        color: #000 !important;
        border-bottom: none !important;
        &:nth-child(2) {
          width: 10%;
        }
        &:nth-child(4) {
          width: 9%;
          @media screen and (min-width: 781px) and (max-width: 1130px) {
            width: 14%;
          }
          @media screen and (min-width: 600px) and (max-width: 780px) {
            width: 20%;
          }
        }
        &:nth-child(5) {
          width: 8%;
        }
        &:nth-child(6) {
          width: 9%;
        }
        &:nth-child(7) {
          width: 12%;
        }
        &:nth-child(8) {
          width: 8%;
        }
        .v-simple-checkbox {
          position: absolute;
          width: 20px;
          height: 20px;
          left: 24px;
          @media screen and (max-width: 630px) {
            left: 17px;
          }
          .v-simple-checkbox {
            width: 20px;
            height: 20px;
          }
          .v-input--selection-controls__ripple {
            width: 20px;
            height: 20px;
            top: -7px;
            left: -7px;
          }
          .v-input--selection-controls__input {
            width: 20px;
            height: 20px;
            position: relative;
            overflow: hidden;
            border-radius: 5px;
            margin-left: 0 !important;
          }
          .mdi::before {
            width: 20px;
            height: 20px;
          }
        }
      }
      & tbody {
        & tr:nth-child(odd) {
          position: relative;
          &:after {
            content: '';
            border-radius: 6px;
            background-color: #f9f8f8;
            position: absolute;
            display: block !important;
            width: 100%;
            height: 100%;
            top: 0;
            left: 0;
          }
        }
        & tr {
          background: none !important;
        }
        & td {
          padding: 15px 16px !important;
          position: relative;
          z-index: 1;
          border: none !important;

          .v-input--selection-controls__input {
            margin-left: 0 !important;
          }
          .v-simple-checkbox {
            bottom: inherit !important;
          }
          &:first-child {
            padding: 15px 10px 15px 25px !important;
            @media screen and (max-width: 630px) {
              padding: 5px 16px !important;
            }
          }
          @media screen and (max-width: 630px) {
            padding: 5px 16px !important;
          }
        }
      }
    }
  }
  .v-image {
    border-radius: 6px;
  }
  .v-data-footer {
    border-top: none !important;
    right: 0;
    padding: 5px 0;
    max-width: 45%;
    width: max-content;
    justify-content: flex-end;
    @media screen and (max-width: 920px) {
      max-width: 100%;
      justify-content: center;
      width: 100%;
      .v-data-footer__select {
        margin-left: 0;
      }
    }
    &__select {
      color: #7a7a7a;
      font-size: 12px;
      font-weight: 400;
    }
  }
  .pencil-outline-icon {
    margin-right: 20px !important;
    cursor: pointer;
  }
  .material-design-icon {
    cursor: pointer;
  }
  .mdi-chevron-right {
    &::before {
      content: '';
      width: 8px;
      height: 15px;
      background-image: url(../assets/chevron-right.svg);
    }
  }
  .mdi-chevron-left {
    &::before {
      content: '';
      width: 8px;
      height: 15px;
      background-image: url(../assets/chevron-left.svg);
    }
  }
  .v-application--is-ltr .v-data-footer__icons-before .v-btn:last-child {
    margin-right: 0;
  }
  .v-simple-checkbox {
    .mdi:before {
      color: #fff0;
      border: 1px solid #7d7c7c;
      border-radius: 5px;
      margin: 0;
      padding: 0;
    }
    .mdi.mdi-checkbox-marked {
      background-image: url(../assets/checkbox-checked.svg);
      background-size: cover;
    }
  }
  .mdi-set {
    color: #000;
  }
  .v-data-footer__select .v-select__selections .v-select__selection--comma {
    font-size: 16px;
  }
  .v-application--is-ltr .v-data-footer__pagination {
    color: #7a7a7a;
    margin-right: 20px;
    margin-left: 60px;
  }
  .v-application--is-ltr .v-data-footer__select .v-select {
    margin: 13px 0 13px 20px;
  }
  .v-application .py-5 {
    padding-top: 30px !important;
    padding-bottom: 28px !important;
  }
  .v-btn {
    &.v-btn--has-bg {
      background: none;
    }
    &--is-elevated {
      box-shadow: none;
    }
    &__content {
      font-size: 16px;
    }
    &--is-elevated {
      color: #000;
    }
    &:not(.v-btn--round).v-size--default {
      height: 40px;
    }
    &.v-btn--disabled.v-btn--has-bg {
      background-color: #b3b3b3 !important;
      color: #fff !important;
    }
  }
  .v-chip {
    border-radius: 5px;
    background: #fff !important;
    color: #000;
    &:hover {
      background: #fff !important;
      &::before {
        display: none;
      }
    }
  }
  .v-price {
    font-weight: 600;
  }
  .v-input--selection-controls__input {
    margin-right: 0;
    margin-left: 8px;
    @media screen and (max-width: 764px) {
      margin-left: 0;
    }
  }
  .btn-return {
    background: #4fbc47 !important;
    color: #fff !important;
  }
}

@media screen and (max-width: 768px) {
  .quantity-dialog {
    padding: 10px !important;
    .v-card__title {
      padding: 16px 0 10px !important;
    }
    .v-card__text {
      padding: 0 0 10px !important;
      .container {
        padding: 15px 0 0 !important;
        .row {
          margin: -10px !important;
        }
      }
    }
    .v-card__actions {
      padding: 0 !important;
    }
  }
}
.quantity-dialog {
  .v-label--active {
    top: 6px;
    left: -10px !important;
  }
  .v-input__slot {
    fieldset {
      background-color: #fff;
      border: none !important;
    }
  }
}
.v-application {
  background-color: #fff;
}
//themes

.theme-dark {
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }
  .v-application {
    background-color: #221f2e;
    .white {
      background-color: #221f2e !important;
    }

    .v-input__slot {
      fieldset {
        background-color: #2a335b;
      }
    }
  }
  .container {
    color: #fff;
    .v-data-table {
      &__wrapper {
        & th {
          color: #fff !important;
        }
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #2a335b;
            }
          }
          & td {
            color: #fff;
          }
        }
      }
    }
    .v-chip {
      background: #1b254e !important;
      color: #fff;
    }
    .btn-anulat {
      background: #1d64bf !important;
      color: #fff !important;
    }
    .btn-return.v-btn--disabled {
      background: #2b524c !important;
      color: #5f6683 !important;
    }
    .btn-return {
      background: #4fbc47 !important;
      color: #fff !important;
    }
    .mdi-chevron-left::before,
    .mdi-chevron-right::before {
      filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
        contrast(105%);
    }
    .v-select__selection {
      color: #fff;
    }
    .v-select {
      .v-input__slot:before {
        border-color: #fff;
      }
      &:hover {
        .v-input__slot:before {
          border-color: #fff !important;
        }
      }
    }
    .v-data-footer__select {
      color: #9296aa;
    }
    .v-data-footer__pagination {
      color: #9296aa;
    }
  }
  .mdi-arrow-up.mdi:before {
    color: #fff !important;
  }
  .v-data-table {
    background: transparent;
  }
  .v-simple-checkbox {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
    .mdi:before {
      opacity: 0.5;
    }
  }
  .v-toolbar__title {
    color: #fff;
  }
  .pencil-outline-icon,
  .material-design-icon {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
}

.theme-light-green {
  .v-application,
  .quantity-dialog {
    background-color: #d7e3d7 !important;

    .v-input__slot {
      fieldset {
        background-color: #e2ede2;
      }
    }
  }
  .container {
    .v-toolbar.v-toolbar--flat {
      background-color: #d7e3d7 !important;
    }
    .v-data-table {
      background: #d7e3d7;
      &__wrapper {
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #e2ede2;
            }
          }
        }
      }
    }
    .v-chip {
      background-color: #d7e3d7 !important;
    }

    .btn-anulat {
      background: #fff !important;
      color: #000;
    }
    .btn-return.v-btn--disabled {
      background: #97c497 !important;
      color: #fff !important;
    }
    .btn-return {
      background: #4fbc47 !important;
      color: #fff !important;
    }
  }
}

.theme-green {
  .v-application,
  .quantity-dialog {
    background-color: #0a5133 !important;
    .white {
      background-color: #0a5133 !important;
    }
    .v-input__slot {
      fieldset {
        background-color: #2f6742;
      }
    }
  }
  .container {
    color: #fff;
    .v-data-table {
      &__wrapper {
        & th {
          color: #fff !important;
        }
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #2f6742;
            }
          }
          & td {
            color: #fff;
          }
        }
      }
    }
    .v-chip {
      background: #0e5133 !important;
      color: #fff;
    }
    .btn-anulat {
      background: #fff !important;
      color: #000;
    }
    .btn-return.v-btn--disabled {
      background: #51792e !important;
      color: #083924 !important;
    }
    .btn-return {
      background: #f7d523 !important;
      color: #000 !important;
    }
    .mdi-chevron-left::before,
    .mdi-chevron-right::before {
      filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
        contrast(105%);
    }
    .v-select__selection {
      color: #fff;
    }
    .v-select {
      .v-input__slot:before {
        border-color: #fff;
      }
      &:hover {
        .v-input__slot:before {
          border-color: #fff !important;
        }
      }
    }
    .v-data-footer__select {
      color: #89ab9d;
    }
    .v-data-footer__pagination {
      color: #89ab9d;
    }
  }
  .mdi:before {
    color: #fff;
  }
  .v-data-table {
    background: transparent;
  }
  .v-simple-checkbox {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
    .mdi:before {
      opacity: 0.5;
    }
  }
  .v-toolbar__title {
    color: #fff;
  }
  .pencil-outline-icon,
  .material-design-icon {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
}

.theme-light-blue {
  .v-application,
  .quantity-dialog {
    background-color: #e6eff7 !important;

    .v-input__slot {
      fieldset {
        background-color: #f4f9fe;
      }
    }
  }
  .container {
    .v-toolbar.v-toolbar--flat {
      background-color: #e6eff7 !important;
    }
    .v-data-table {
      background: #e6eff7;
      &__wrapper {
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #f4f9fe;
            }
          }
        }
      }
    }
    .v-chip {
      background-color: #e6eff7 !important;
    }

    .btn-anulat {
      background: #fff !important;
      color: #000;
    }
    .btn-return.v-btn--disabled {
      background: #b2d7f9 !important;
      color: #fff !important;
    }
    .btn-return {
      background: #39a0ff !important;
      color: #fff !important;
    }
  }
}
.theme-dark {
  .v-application {
    background-color: #221f2e;
    .white {
      background-color: #221f2e !important;
    }
    .v-input__slot {
      fieldset {
        background-color: #2e2b3f;
      }
    }
  }
  .container {
    color: #fff;
    .v-data-table {
      &__wrapper {
        & th {
          color: #fff !important;
        }
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #2e2b3f;
            }
          }
          & td {
            color: #fff;
          }
        }
      }
    }
    .v-chip {
      background: #221f2e !important;
      color: #fff;
    }
    .btn-anulat {
      background: #ff6d3e !important;
      color: #fff;
    }
    .btn-return.v-btn--disabled {
      background: #2e4e35 !important;
      color: #5f6683 !important;
    }
    .btn-return {
      background: #2ab500 !important;
      color: #fff !important;
    }
    .mdi-chevron-left::before,
    .mdi-chevron-right::before {
      filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
        contrast(105%);
    }
    .v-select__selection {
      color: #fff;
    }
    .v-select {
      .v-input__slot:before {
        border-color: #3d3b48;
      }
      &:hover {
        .v-input__slot:before {
          border-color: #3d3b48 !important;
        }
      }
    }
    .v-data-footer__select {
      color: #9296aa;
    }
    .v-data-footer__pagination {
      color: #9296aa;
    }
  }
  .mdi:before {
    color: #fff;
  }
  .v-data-table {
    background: transparent;
  }
  .v-simple-checkbox {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
    .mdi:before {
      opacity: 0.5;
    }
  }
  .v-toolbar__title {
    color: #fff;
  }
  .pencil-outline-icon,
  .material-design-icon {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
}
.quantity-dialog {
  .v-card__text {
    padding: 16px !important;
  }
  .v-card__title {
    display: flex;
    justify-content: space-between;
    width: 100%;
  }
  img {
    cursor: pointer;
  }
}
.product-table-description {
  display: flex;
  margin-top: 0.5rem;
  @media screen and (max-width: 600px) {
    display: none;
  }
}
.product-table-desc-title {
  font-weight: 900;
}
.product-table-desc {
  display: flex;
  justify-content: center;
  align-items: center;
  .v-chip {
    margin: 0rem 1rem;
  }
}
.theme-white {
  .v-application {
    background-color: #fff;
  }

  .container {
    .v-application--wrap {
      .v-input--checkbox {
        .v-input__slot {
          border: none !important;
        }
      }
    }
  }
}
.container--fluid {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.theme-blue {
  .v-application {
    .container {
      .primary--text {
        color: #fff !important;
        caret-color: #fff !important;
      }
    }
  }
  .dialog {
    .v-input__slot {
      border-width: none;
      border-style: none;
      border-color: none;
    }
    .v-input {
      input {
        color: #fff;
      }
    }
  }
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }
  .v-application {
    background-color: #1b254e;
    .white {
      background-color: #1b254e !important;
    }

    .v-input__slot {
      fieldset {
        background-color: #2a335b;
      }
    }
  }
  .container {
    color: #fff;
    .v-data-table {
      &__wrapper {
        & th {
          color: #fff !important;
        }
        & tbody {
          & tr {
            &:hover {
              background: transparent !important;
            }
          }
          & tr:nth-child(odd) {
            &::after {
              background-color: #2a335b;
            }
          }
          & td {
            color: #fff;
          }
        }
      }
    }
    .v-chip {
      background: #1b254e !important;
      color: #fff;
    }
    .btn-anulat {
      background: #1d64bf !important;
      color: #fff !important;
    }
    .btn-return.v-btn--disabled {
      background: #2b524c !important;
      color: #5f6683 !important;
    }
    .btn-return {
      background: #4fbc47 !important;
      color: #fff !important;
    }
    .mdi-chevron-left::before,
    .mdi-chevron-right::before {
      filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
        contrast(105%);
    }
    .v-select__selection {
      color: #fff;
    }
    .v-select {
      .v-input__slot:before {
        border-color: #fff;
      }
      &:hover {
        .v-input__slot:before {
          border-color: #fff !important;
        }
      }
    }
    .v-data-footer__select {
      color: #9296aa;
    }
    .v-data-footer__pagination {
      color: #9296aa;
    }
  }
  .mdi-arrow-up.mdi:before {
    color: #fff !important;
  }

  .v-data-table {
    background: transparent;
  }
  .v-simple-checkbox {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
    .mdi:before {
      opacity: 0.5;
    }
  }
  .v-toolbar__title {
    color: #fff;
  }
  .pencil-outline-icon,
  .material-design-icon {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
}

.theme-blue {
  .products-list .text-subtitle-1 {
    color: white;
  }
  .progress-linear {
    background-color: #f79123 !important;
    border-color: #f79123 !important;
  }
  .btn-anulat {
    background: #1d64bf !important;
    color: #fff !important;
  }
  .btn-return.v-btn--disabled {
    background: #2b524c !important;
    color: #5f6683 !important;
  }
  .btn-return {
    background: #4fbc47 !important;
    color: #fff !important;
  }
  .dialog {
    color: white !important;
    background-color: #1b254e !important;

    .product-variant {
      background-color: #1b254e !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #1b254e !important;
    }
    .products-search {
      background-color: #1b254e;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2a335b !important;
      color: white !important;

      .v-application .info--text,
      .v-icon.v-icon {
        color: #fff !important;
        caret-color: #fff !important;
      }
    }
  }

  .v-application--is-ltr {
    fieldset {
      background: #2a335b;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: 1px solid #f7d523 !important;
      }
    }
    .v-text-field .v-label {
      color: #fff;
    }
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2a335b !important;
  }
  .container--fluid {
    display: flex;
    flex-direction: column;
    gap: 20px;
    .row:not(:last-child) {
      background-color: #2a335b;
    }
    .v-subheader {
      color: #fff;
    }
  }
  .v-select-wrap {
    .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
      background: #4c5475 !important;
    }
  }
  .mdi:before {
    color: #fff0;
  }
  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .container--fluid .btn-change .v-icon--left::before {
    filter: brightness(0) saturate(100%) invert(15%) sepia(33%) saturate(1415%) hue-rotate(192deg) brightness(86%)
      contrast(96%);
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff !important;
    & a {
      color: #fff !important;
    }
  }
  .v-btn.v-btn--back {
    background: #1d64bf !important;
    color: #fff !important;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #4fbc47 !important;
  }
}

.theme-light-green {
  .products-list .text-subtitle-1 {
    color: black;
  }
  .progress-linear {
    background-color: #8fc026 !important;
    border-color: #8fc026 !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #97c497 !important;
    color: #fff !important;
  }
  .btn-return {
    background: #4fbc47 !important;
    color: #fff !important;
  }
  .dialog {
    background-color: #d7e3d7 !important;

    .product-variant {
      background-color: #d7e3d7 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #d7e3d7 !important;
    }
    .products-search {
      background-color: #d7e3d7;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }

    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #e2ede2 !important;

      .v-sheet.v-alert .v-icon.v-icon {
        color: #8fc026 !important;
        caret-color: #8fc026 !important;
      }
    }
  }

  .v-application--is-ltr {
    fieldset {
      background: #e2ede2;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: 1px solid #fff !important;
      }
    }
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #e2ede2 !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #fff !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #e2ede2 !important;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #1a7b00 !important;
  }
}

.theme-green {
  .close-icon {
    cursor: pointer;
    path {
      fill: #fff;
    }
  }
  .products-list .text-subtitle-1 {
    color: white;
  }
  .progress-linear {
    background-color: white !important;
    border-color: white !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #51792e !important;
    color: #083924 !important;
  }
  .btn-return {
    background: #f7d523 !important;
    color: #000 !important;
  }
  .dialog {
    color: white !important;
    background-color: #0a5133 !important;

    .product-variant {
      background-color: #0a5133 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #0a5133 !important;
    }
    .products-search {
      background-color: #0a5133;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2f6742 !important;
      color: white !important;

      .v-application .info--text,
      .v-icon.v-icon {
        color: #fff !important;
        caret-color: #fff !important;
      }
    }
  }

  .v-label {
    color: #fff;
  }
  .v-application--is-ltr {
    fieldset {
      background: #2f6742;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: 1px solid #dac525 !important;
      }
    }
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2f6742 !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #0e5133 !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #2f6742 !important;
  }
  .container--fluid .v-subheader {
    color: #fff;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #f7d523 !important;
    color: #000;
  }

  .container--fluid .btn-change .v-icon--left::before {
    filter: brightness(0) saturate(100%) invert(49%) sepia(93%) saturate(604%) hue-rotate(41deg) brightness(95%)
      contrast(90%);
  }
  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .v-btn.v-btn--has-bg {
    background-color: #fff;
  }
  .container--fluid .v-btn__content {
    color: #79ab15 !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #e2e2e2;
    }
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff;
    & a {
      color: #fff;
    }
  }
  .mdi-arrow-up.mdi:before {
    color: #fff !important;
  }
}

.theme-light-blue {
  .products-list .text-subtitle-1 {
    color: black;
  }
  .progress-linear {
    background-color: #39a0ff !important;
    border-color: #39a0ff !important;
  }
  .btn-anulat {
    background: #fff !important;
    color: #000;
  }
  .btn-return.v-btn--disabled {
    background: #b2d7f9 !important;
    color: #fff !important;
  }
  .btn-return {
    background: #39a0ff !important;
    color: #fff !important;
  }
  .dialog {
    background-color: #e6eff7 !important;

    .product-variant {
      background-color: #e6eff7 !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #e6eff7 !important;
    }
    .products-search {
      background-color: #e6eff7;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }

    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #f4f9fe !important;

      .v-sheet.v-alert .v-icon.v-icon {
        color: #39a0ff !important;
        caret-color: #39a0ff !important;
      }
    }
  }

  .v-label {
    color: #000;
  }
  .v-application--is-ltr {
    fieldset {
      background: #f4f9fe;
    }
    .v-text-field.v-input--is-focused {
      fieldset {
        border: 1px solid #fff !important;
      }
    }
  }

  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #f4f9fe !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #fff !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #f4f9fe !important;
  }
  .container--fluid .v-subheader {
    color: #000;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #39a0ff !important;
    color: #fff;
  }

  .v-btn--back.v-btn.v-btn--has-bg:not(.btn-return) {
    background-color: #fff !important;
  }
  /* .v-btn.v-btn--has-bg :not(.btn-return) {
    background-color: #2a335b !important;
  } */
  .container--fluid .v-btn__content {
    color: #fff !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #676767;
    }
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #000;
    & a {
      color: #000;
    }
  }
}

.theme-dark {
  .dialog {
    .v-input__slot {
      border-width: none;
      border-style: none;
      border-color: none;
    }
  }
  .products-list .text-subtitle-1 {
    color: white;
  }
  .progress-linear {
    background-color: #f79123 !important;
    border-color: #f79123 !important;
  }
  .btn-anulat {
    background: #ff6d3e !important;
    color: #fff;
  }
  .btn-return.v-btn--disabled {
    background: #2e4e35 !important;
    color: #5f6683 !important;
  }
  .btn-return {
    background: #2ab500 !important;
    color: #fff !important;
  }
  .dialog {
    color: white !important;
    background-color: #221f2e !important;

    .product-variant {
      background-color: #221f2e !important;
    }
    .float-price {
      box-shadow: -19px 0px 5px -2px #221f2e !important;
    }
    .products-search {
      background-color: #221f2e;
      .v-input__slot {
        /* background-color: #fff; */
        border: none;
      }
    }
    .v-image__image.v-image__image--cover {
      filter: invert(1);
    }
    .product-img .v-image__image.v-image__image--cover {
      filter: none;
    }
    .v-card,
    .v-input,
    .product-wrapper .product-variant .product-variant-details {
      color: white !important;
    }
    .product-wrapper .v-icon.v-icon {
      color: #fff !important;
      caret-color: #fff !important;
    }
    .v-sheet.v-alert,
    .product-wrapper {
      background-color: #2e2b3f !important;
      color: white !important;
    }

    .v-application .info--text,
    .v-sheet.v-alert .v-icon.v-icon {
      color: #f79123 !important;
      caret-color: #f79123 !important;
    }
  }

  .v-label {
    color: #828289;
  }
  .v-select__slot {
    .v-label {
      color: #fff;
    }
  }
  .v-application.v-application--is-ltr {
    .quantity-dialog {
      .v-text-field.v-input--is-focused {
        .v-label {
          color: #fff !important;
          opacity: 0.48;
          top: 6px !important;
          left: -10px !important;
        }
      }
    }
  }
  .v-application.v-application--is-ltr .v-text-field.v-input--is-focused .v-label {
    color: #fff !important;
    opacity: 0.48;
  }
  .v-application--is-ltr {
    fieldset {
      background: #2e2b3f;
    }

    .v-text-field.v-input--is-focused {
      .v-input__slot {
        border-color: #d27f29 !important;
      }
      fieldset {
        border: 1px solid #d27f29 !important;
        border-color: #d27f29 !important;
      }
    }
  }
  .second-tab .v-text-field .v-label {
    color: #fff !important;
    opacity: 0.48;
  }
  .v-text-field--solo.v-select-return > .v-input__control > .v-input__slot {
    background: #2e2b3f !important;
  }
  .v-row-wrap .v-input__control > .v-input__slot {
    background: #4f4d5e !important;
  }
  .container--fluid .row:not(:last-child) {
    background-color: #2e2b3f !important;
  }
  .container--fluid .v-subheader {
    color: #fff;
  }
  .v-btn.v-btn--back {
    background: #fff !important;
    color: #000;
  }
  .v-btn--return.v-btn--is-elevated.v-btn--has-bg {
    background: #2ab500 !important;
    color: #fff;
  }

  .v-btn--back.v-btn.v-btn--has-bg:not(.btn-return) {
    background-color: #f96c3f !important;
    .v-btn__content {
      color: #fff;
    }
  }
  .v-btn.v-btn--has-bg {
    // background-color: #4f4d5e !important;
  }
  .container--fluid .v-btn__content {
    color: #fff !important;
  }
  .v-input--selection-controls__input {
    .mdi:before {
      border: 1px solid #676767;
    }
  }

  .v-input__append-inner .v-input__icon--append,
  .v-input--selection-controls__input .mdi.mdi-checkbox-marked {
    filter: brightness(0) saturate(100%) invert(100%) sepia(100%) saturate(0%) hue-rotate(210deg) brightness(105%)
      contrast(105%);
  }
  .v-application--wrap .v-slot--term .v-input__slot .v-label {
    color: #fff;
    & a {
      color: #fff;
    }
  }
}

.v-radio .v-input--selection-controls__input .mdi:before {
  border: none !important;
  color: #000;
}

::-webkit-scrollbar-track {
  -webkit-box-shadow: none;
  background-color: #fff;
}

::-webkit-scrollbar {
  width: 8px;
  background-color: #f5f5f5;
}

::-webkit-scrollbar-thumb {
  border-radius: 10px;
  -webkit-box-shadow: none;
  background-color: #d8d8d8;
}

.theme-blue {
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #2a335b;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #2a335b;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #fff0;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.theme-light-green {
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #e2ede2;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #e2ede2;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #8fc026;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #000;
  }
}
.theme-green {
  .dialog {
    .v-input__slot {
      border-width: none;
      border-style: none;
      border-color: none;
    }
  }
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #2f6742;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #2f6742;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #062c1b;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.theme-light-blue {
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #f4f9fe;
  }

  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #000;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #f4f9fe;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #39a0ff;
  }
}
.theme-dark {
  ::-webkit-scrollbar-track {
    -webkit-box-shadow: none;
    background-color: #4f4d5e;
  }

  ::-webkit-scrollbar {
    width: 8px;
    background-color: #4f4d5e;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    -webkit-box-shadow: none;
    background-color: #221f2ed2;
  }
  .v-radio .v-input--selection-controls__input .mdi:before {
    border: none !important;
    color: #fff;
  }
}
.v-data-table-header-mobile__select {
  justify-content: flex-start !important;
  align-items: flex-start !important;
}
.quantity-dialog {
  @media screen and (max-width: 768px) {
    .v-input__slot {
      min-height: 40px !important;
    }
  }
}
@media screen and (max-width: 768px) {
  .v-dialog__content {
    .v-dialog {
      .quantity-dialog {
        .v-card__text {
          margin-top: 20px;
          padding: 0 10px !important;
        }
      }
    }
  }
}
.modal-title {
  display: flex;
  justify-content: space-between;
  width: 100%;
}

.close-icon {
  cursor: pointer;
  width: 15px;
  height: 15px;
  path {
    fill: #000;
  }
}
@media screen and (max-width: 768px) {
  .v-dialog {
    margin: 12px;
    .dialog {
      .v-card__title {
        padding: 10px !important;
        .text-h5 {
          font-size: 16px !important;
          line-height: 19px !important;
        }
      }
      .products-search {
        padding: 0 10px !important;
      }
      .v-sheet.v-alert {
        padding: 20px;
        margin: 10px;
      }
      .v-card__actions {
        padding: 10px !important;
        & > .v-btn.v-btn {
          height: 40px;
        }
      }
      .v-card__text {
        padding: 0 24px !important;

        .v-text-field__slot input {
          font-size: 14px;
        }
      }
      .product-wrapper {
        margin: 0 2px 0 10px;

        .product {
          .v-image {
            max-width: 35px !important;
            min-width: 35px !important;
          }
          .name {
            font-size: 12px;
            line-height: 14px;
          }
          .v-responsive__content {
            width: 12px !important;
          }
          .expand {
            margin-right: 5px;
            min-width: max-content;

            .expand__text {
              font-size: 12px;
              line-height: 14px;
              margin-left: 10px;
            }
            .expand__arrow {
              .v-image__image {
                background-size: 12px 6px;
              }
              .v-image {
                max-width: 12px !important;
                min-width: 12px !important;
              }
            }
          }
        }
        .product-variant .v-input--selection-controls__input {
          max-height: 16px;
          max-width: 16px;

          .v-icon {
            font-size: 19px;
          }
        }
      }
      .v-input--radio-group__input {
        gap: 7px;
      }
    }
  }

  .form-types {
    .row {
      row-gap: 0 !important;
      .v-text-field.error--text.v-input {
        margin-bottom: 10px;
        .v-input__control .v-input__slot {
          border: 1px solid #ff5252 !important;
          fieldset {
            border: none !important;
          }
        }
      }
      .v-text-field__details {
        bottom: -28px !important;
      }
      .v-input__slot {
        min-height: 40px;
        font-size: 14px;
        .v-label {
          font-size: 14px;
        }
      }
      .v-text-field.v-text-field--solo .v-input__control {
        max-height: 40px;
        .v-label {
          font-size: 14px;
        }
      }
      .v-textarea {
        .v-label {
          font-size: 14px;
        }
      }
      .v-text-field--outlined .v-label {
        top: 11px;
      }

      .v-text-field.v-input--is-focused .v-label {
        top: 3px !important;
      }
    }
    .container--fluid .btn-change {
      min-width: auto !important;
      max-width: max-content !important;
    }
  }
}
.dialog {
  background-color: #fff !important;
  .v-card__text {
    padding: 0 24px !important;
  }
  .v-sheet.v-alert {
    margin: 24px;
    background-color: #f7f7f7 !important;
    border-radius: 5px !important;
    border: none !important;
    padding: 20px;
    color: black !important;
    .v-alert__content {
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 10px;
      .title {
        font-size: 16px !important;
        line-height: 18px;
        font-weight: 400;
      }
      .subtitle {
        font-size: 12px;
        line-height: 14px;
        font-weight: 300;
      }
    }
    .mdi:before {
      color: #39a0ff !important;
    }
  }
  .v-card__title {
    padding: 24px !important;
  }
  .v-text-field__details {
    display: none;
  }
  .v-input--radio-group__input {
    gap: 8px;
  }
  .product-wrapper {
    margin: 0 16px 0 25px;
    padding: 8px;
    border-radius: 5px;
    background-color: #f7f7f7;
    gap: 8px;
    display: flex;
    flex-direction: column;
    .product {
      border-bottom: none;
      padding: 0;
      .v-image {
        border-radius: 5px;
      }
      .name {
        font-size: 14px;
        line-height: 1.7rem;
      }
      .expand {
        margin-left: auto;
        display: flex;
        align-items: center;
        margin-right: 15px;
        .expand__text {
          font-size: 14px;
          text-decoration: underline;
        }
        .expand__arrow {
          margin-left: 26px;
          transform: rotateX(180deg);
          &.expandedBlock {
            transform: none;
          }
        }
      }
    }
    .product-variant {
      border-bottom: none;
      background-color: white;
      border-radius: 5px;
      height: 30px;
      padding: 5px;
      font-size: 12px;
      line-height: 14px;
      display: flex;
      align-items: center;
      cursor: pointer;

      .v-input .v-label,
      .product-variant-details {
        font-size: 12px;
        line-height: 20px;
        color: black;
        display: flex;
        align-items: center;
        gap: 8px;
        justify-content: space-between;
        width: calc(100% - 48px);
        .float-left {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: clip;
          max-width: 145px;
          margin-right: -3px;
        }
        .float-price {
          font-weight: 600;
          box-shadow: -19px 0px 5px -2px #fff;
        }
        .float-currency {
          font-weight: 300;
        }
        .float-right {
          min-width: max-content;
        }
      }
      .v-input--selection-controls__input {
        max-height: 20px;
        max-width: 20px;
        input {
          display: none;
        }
      }
      .v-input--selection-controls__ripple {
        display: none;
      }

      .expand__arrow {
        margin-left: 6px;
      }
      .radio {
        margin-bottom: 0 !important;
      }

      &.expanded {
        height: auto;

        .expand__arrow {
          transform: rotateX(180deg);
        }
        .float-left {
          white-space: inherit;
          overflow: auto;
          text-overflow: none;
          max-width: 140px;
        }
      }
    }
  }
  .v-card__actions {
    display: flex;
    padding: 24px !important;
    align-items: center;
    justify-content: flex-end;
    gap: 17px;
  }
  .btn-anulat {
    margin: 0 !important;
  }
  .btn-return.v-btn--disabled {
    color: #fff !important;
  }
  .btn-return {
    margin: 0 !important;
  }
}

.theme-green {
  .dialog {
    color: white !important;
    background-color: #0a5133 !important;
    .v-input {
      input {
        color: #000;
      }
    }

    fieldset {
      background: #fff !important;
    }
  }
}

.dialog {
  .v-input__slot {
    border-width: 1px;
    border-style: solid;
    border-color: #c9cccf;
  }
  .v-input {
    input {
      color: #000;
    }
  }

  fieldset {
    background: #fff;
  }
}

.container {
  .v-application--wrap {
    .v-data-table-header-mobile__wrapper {
      .v-input {
        .v-input__control {
          .v-input__slot {
            border-width: none !important;
            border-style: none !important;
            border-color: none !important;
          }
        }
      }
    }
    .v-data-footer {
      .v-input {
        .v-input__control {
          .v-input__slot {
            border-width: none !important;
            border-style: none !important;
            border-color: none !important;
          }
        }
      }
    }
  }
}

.theme-white {
  .dialog {
    .v-input__slot {
      border-width: 1px !important;
      border-style: solid !important;
      border-color: #c9cccf !important;
    }
    .v-input {
      input {
        color: #000;
      }
    }

    fieldset {
      background: #fff !important;
    }
  }
}
.theme-light-green {
  .dialog {
    .v-input__slot {
      border-width: none;
      border-style: none;
      border-color: none;
    }
    background-color: #d7e3d7 !important;
    .v-input {
      input {
        color: #000;
      }
    }

    fieldset {
      background: #fff !important;
    }
  }
}
.theme-light-blue {
  .dialog {
    .v-input__slot {
      border-width: none;
      border-style: none;
      border-color: none;
    }
    .v-input {
      input {
        color: #000;
      }
    }

    fieldset {
      background: #fff !important;
    }
  }
}
.theme-dark {
  .dialog {
    .v-input {
      input {
        color: #000;
      }
    }

    fieldset {
      background: #fff !important;
    }
  }
}
</style>
